import { ICartPrice, IEcommerceServices, getCartServices } from '@model';
import { ECartType, IEcommerceCheckout, IEcommerceProduct } from '@services/ecommerceService';
import { getRevenue, getRevenueWithoutDiscount } from '@utils/math';

import { ICartServiceInfo, ICustomService, IDiscoveryBox, IDiscoveryBoxItem, ILastOrder } from '../../model';
import { IProduct, IServices } from '../../model';
import { convertEcommerceServiceToProduct } from '../utils';

import { createEcommerceDiscoveryBox, createEcommerceDiscoveryBoxProduct } from './discoveryBox';
import { createEcommerceProduct } from './product';
import { createEcommerceCustomService, createEcommerceService } from './service';

/**
 * Creates an eCommerce checkout object based on the provided data.
 *
 * @param products - An array of products to be included in the cart.
 * @param prices - The cart price information.
 * @param services - The services to be added to the cart.
 * @param availableServices - Optional: The available eCommerce services.
 * @param hasFreeDelivery - Optional: Defines if customer has free delivery in cart
 * @returns The eCommerce checkout object with calculated revenue, total value, tax, products, and services.
 */
export const createEcommerceCheckout = (
  products: IProduct[],
  prices: ICartPrice,
  services: IServices,
  availableServices?: IEcommerceServices,
  hasFreeDelivery?: boolean,
): IEcommerceCheckout => {
  let revenue: number = 0;
  let revenueWithoutDiscount: number = 0;
  const ecommerceProducts: IEcommerceProduct[] = [];

  // add to products function init
  const addToProducts = (product: IEcommerceProduct) => {
    ecommerceProducts.push(product);
  };

  // Add to revenue function init
  const addToRevenue = (product: IEcommerceProduct) => {
    const value = getRevenue({ vat: product.vat, priceWithVat: product.fullPrice, count: product.quantity });
    revenue += value;
  };

  // Process products
  products.forEach((product: IProduct) => {
    revenue += getRevenue(product);
    revenueWithoutDiscount += getRevenueWithoutDiscount(product);
    ecommerceProducts.push(createEcommerceProduct(product));
  });

  // process engravings
  products.forEach((product: IProduct) => {
    if (product.engraving !== null && product.engraving !== undefined) {
      processCustomService(product.engraving, product.vat, addToProducts, addToRevenue);
    }
  });

  // Process services
  processServices(services, addToProducts, addToRevenue);

  // Return ecommerce object
  return {
    revenue: Math.round((revenue + Number.EPSILON) * 100) / 100,
    revenueWithoutDiscount,
    totalValue: Math.round((prices.priceItemSummary + Number.EPSILON) * 100) / 100,
    tax: Math.round((prices.priceItemSummary - revenue + Number.EPSILON) * 100) / 100,
    cartType: ECartType.Cart,
    products: ecommerceProducts,
    services: getCartServices(availableServices),
    freeDelivery: hasFreeDelivery,
  };
};

/**
 * Processes the services and adds them to the eCommerce products and calculates the revenue.
 *
 * @param services - The services to be processed.
 * @param addToProducts - The function to add products to the eCommerce products array.
 * @param addToRevenue - The function to add revenue for the processed products.
 */
const processServices = (
  services: IServices,
  addToProducts: (product: IEcommerceProduct) => void,
  addToRevenue: (product: IEcommerceProduct) => void,
) => {
  if (services.insurance) {
    processService(services.insurance, addToProducts, addToRevenue);
  }

  if (services.fundRaising) {
    processService(services.fundRaising, addToProducts, addToRevenue);
  }

  if (services.ecoPackaging) {
    processService(services.ecoPackaging, addToProducts, addToRevenue);
  }

  if (services.giftPackagings) {
    services.giftPackagings.forEach((giftPackaging: ICartServiceInfo) => {
      processService(giftPackaging, addToProducts, addToRevenue);
    });
  }

  if (services.discoveryBox) {
    services.discoveryBox.forEach((discoveryBox) => {
      processDiscoveryBox(discoveryBox, addToProducts, addToRevenue);
      discoveryBox.products.forEach((discoveryProduct) => {
        processDiscoveryBoxProduct(discoveryProduct, addToProducts, addToRevenue);
      });
    });
  }
};

/**
 * Processes a specific service item and converts it to an eCommerce product.
 *
 * @param serviceItem - The service item to be processed.
 * @param addToProducts - The function to add products to the eCommerce products array.
 * @param addToRevenue - The function to add revenue for the processed products.
 */
const processService = (
  serviceItem: ICartServiceInfo,
  addToProducts: (product: IEcommerceProduct) => void,
  addToRevenue: (product: IEcommerceProduct) => void,
) => {
  const product = convertEcommerceServiceToProduct(createEcommerceService(serviceItem));
  addToProducts(product);
  addToRevenue(product);
};

/**
 * Processes a specific custom service item and converts it to an eCommerce product.
 *
 * @param serviceItem - The service item to be processed.
 * @param addToProducts - The function to add products to the eCommerce products array.
 * @param addToRevenue - The function to add revenue for the processed products.
 */
const processCustomService = (
  serviceItem: ICustomService,
  vat: number,
  addToProducts: (product: IEcommerceProduct) => void,
  addToRevenue: (product: IEcommerceProduct) => void,
) => {
  const product = convertEcommerceServiceToProduct(createEcommerceCustomService(serviceItem, vat));
  addToProducts(product);
  addToRevenue(product);
};

export const createEcommerceOaltCheckout = (
  lastOrder: ILastOrder,
  products: IProduct[],
  pricesSummary: ICartPrice,
  services: IServices,
  availableServices?: IEcommerceServices,
): IEcommerceCheckout => {
  const ecommerceCheckout: IEcommerceCheckout = createEcommerceCheckout(
    products,
    pricesSummary,
    services,
    availableServices,
  );

  ecommerceCheckout.paymentType = lastOrder.paymentInfo.paymentName;
  ecommerceCheckout.deliveryType = lastOrder.deliveryInfo.deliveryName;
  ecommerceCheckout.shipping = lastOrder.deliveryInfo.deliveryPrice;
  ecommerceCheckout.providerType = lastOrder.paymentInfo.providerType;

  return ecommerceCheckout;
};

/**
 * Processes a discovery box and converts it to an eCommerce product.
 *
 * @param discoveryBox - The discovery box to be processed.
 * @param addToProducts - The function to add products to the eCommerce products array.
 * @param addToRevenue - The function to add revenue for the processed products.
 */
const processDiscoveryBox = (
  discoveryBox: IDiscoveryBox,
  addToProducts: (product: IEcommerceProduct) => void,
  addToRevenue: (product: IEcommerceProduct) => void,
) => {
  const product = createEcommerceDiscoveryBox(discoveryBox);
  addToProducts(product);
  addToRevenue(product);
};

/**
 * Processes a discovery box product and converts it to an eCommerce product.
 *
 * @param discoveryBoxProduct - The discovery box product to be processed.
 * @param addToProducts - The function to add products to the eCommerce products array.
 * @param addToRevenue - The function to add revenue for the processed products.
 */
const processDiscoveryBoxProduct = (
  discoveryBoxProduct: IDiscoveryBoxItem,
  addToProducts: (product: IEcommerceProduct) => void,
  addToRevenue: (product: IEcommerceProduct) => void,
) => {
  const product = createEcommerceDiscoveryBoxProduct(discoveryBoxProduct);
  addToProducts(product);
  addToRevenue(product);
};
